import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/components/utils/utils';
import { formatVariablePlaceholder } from '@/lib/admin/email-templates/models/email-template.model';

interface VariablesBarProps {
  variables: string[];
  /** Inserisce il placeholder nell'ultimo campo toccato. False = nessun campo attivo. */
  onInsert: (placeholder: string) => boolean;
}

/**
 * Barra delle variabili disponibili del template.
 *
 * `available_variables` è deciso dal backend (è il payload che il trigger passa
 * al mailer): la lista non si modifica da qui. Ogni chip è però cliccabile e
 * inserisce il placeholder nel campo di testo attivo — l'unica azione utile.
 */
export const VariablesBar = ({ variables, onInsert }: VariablesBarProps) => {
  const { t } = useI18n();
  const { toast } = useToast();

  const handleInsert = (variable: string) => {
    if (onInsert(formatVariablePlaceholder(variable))) return;
    toast({
      variant: 'info',
      title: t('emailTemplates.variables.noTargetTitle'),
      description: t('emailTemplates.variables.noTargetDescription'),
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {t('emailTemplates.variables.title')}
      </span>

      {variables.length === 0 ? (
        <span className="text-[11px] italic text-gray-400">
          {t('emailTemplates.variables.empty')}
        </span>
      ) : (
        variables.map((variable) => (
          <button
            key={variable}
            type="button"
            onClick={() => handleInsert(variable)}
            title={t('emailTemplates.variables.insertAction')}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 font-mono text-[11px] text-gray-700 transition-colors hover:border-gray-900 hover:text-gray-900"
          >
            {formatVariablePlaceholder(variable)}
          </button>
        ))
      )}

      <span className={cn('text-[11px] text-gray-400', variables.length === 0 && 'hidden')}>
        {t('emailTemplates.variables.insertHint')}
      </span>
    </div>
  );
};
